import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = [
    "grid", "soundButton", "masterVolume", "masterVolumeLabel",
    "manageToggle", "settingsModal", "settingsTitle",
    "settingsVolume", "settingsVolumeLabel",
    "settingsEqLow", "settingsEqLowLabel",
    "settingsEqMid", "settingsEqMidLabel",
    "settingsEqHigh", "settingsEqHighLabel",
    "settingsLoop"
  ]

  static values = {
    sounds: Array,
    productionId: Number
  }

  FADE_DURATION = 2

  connect() {
    this.audioContext = null
    this.activeSounds = new Map()
    this.audioElements = new Map()
    this.audioGraphs = new Map()
    this.endedHandlers = new Map()
    this.fadeStopTimers = new Map()
    this.soundSettings = new Map()
    this.masterGain = 1.0
    this.manageMode = false
    this.currentSettingsSoundId = null
    this.sortables = []

    this.soundsValue.forEach(s => {
      this.soundSettings.set(s.id, { ...s })
    })

    this._createAudioElements()
  }

  disconnect() {
    for (const soundId of [...this.activeSounds.keys()]) {
      this._stop(soundId, { fade: 0 })
    }
    for (const [, timer] of this.fadeStopTimers) {
      clearTimeout(timer)
    }
    this.sortables.forEach(s => s.destroy())
    for (const [, audio] of this.audioElements) {
      audio.pause()
      audio.removeAttribute("src")
      audio.load()
    }
    if (this.audioContext) this.audioContext.close()
  }

  _createAudioElements() {
    this.soundsValue.filter(s => s.audio_url).forEach(s => {
      const audio = new Audio()
      audio.preload = "auto"
      audio.src = s.audio_url
      this.audioElements.set(s.id, audio)
    })
  }

  _getOrCreateContext() {
    if (!this.audioContext || this.audioContext.state === "closed") {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
    }
    if (this.audioContext.state === "suspended") {
      this.audioContext.resume()
    }
    return this.audioContext
  }

  _ensureAudioGraph(soundId, ctx) {
    if (this.audioGraphs.has(soundId)) return this.audioGraphs.get(soundId)

    const audio = this.audioElements.get(soundId)
    if (!audio) return null

    const mediaSource = ctx.createMediaElementSource(audio)

    const gainNode = ctx.createGain()
    const eqLow = ctx.createBiquadFilter()
    eqLow.type = "lowshelf"
    eqLow.frequency.value = 320

    const eqMid = ctx.createBiquadFilter()
    eqMid.type = "peaking"
    eqMid.frequency.value = 1000
    eqMid.Q.value = 0.5

    const eqHigh = ctx.createBiquadFilter()
    eqHigh.type = "highshelf"
    eqHigh.frequency.value = 3200

    mediaSource.connect(gainNode)
    gainNode.connect(eqLow)
    eqLow.connect(eqMid)
    eqMid.connect(eqHigh)
    eqHigh.connect(ctx.destination)

    const graph = { gainNode, eqLow, eqMid, eqHigh }
    this.audioGraphs.set(soundId, graph)
    return graph
  }

  async togglePlay(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.manageMode) return

    const ctx = this._getOrCreateContext()

    const btn = event.currentTarget
    const soundId = parseInt(btn.dataset.soundId)

    if (this.activeSounds.has(soundId)) {
      this._stop(soundId)
    } else {
      await this._play(soundId, ctx)
    }
  }

  async _play(soundId, ctx) {
    const settings = this.soundSettings.get(soundId)
    if (!settings) return
    if (!ctx) ctx = this._getOrCreateContext()

    const audio = this.audioElements.get(soundId)
    if (!audio) return

    if (this.fadeStopTimers.has(soundId)) {
      clearTimeout(this.fadeStopTimers.get(soundId))
      this.fadeStopTimers.delete(soundId)
    }

    if (this.endedHandlers.has(soundId)) {
      audio.removeEventListener("ended", this.endedHandlers.get(soundId))
      this.endedHandlers.delete(soundId)
    }

    const graph = this._ensureAudioGraph(soundId, ctx)
    if (!graph) return

    graph.gainNode.gain.cancelScheduledValues(ctx.currentTime)
    graph.gainNode.gain.value = (settings.volume || 0.8) * this.masterGain
    graph.eqLow.gain.value = settings.eq_low || 0
    graph.eqMid.gain.value = settings.eq_mid || 0
    graph.eqHigh.gain.value = settings.eq_high || 0

    audio.loop = settings.loop || false
    audio.currentTime = 0

    try {
      await audio.play()
    } catch (e) {
      console.error(`Failed to play sound ${soundId}:`, e)
      return
    }

    const entry = { fadeTimer: null }
    this.activeSounds.set(soundId, entry)
    this._setPlaying(soundId, true)

    const isNarration = settings.category === "narration"
    if (!isNarration && !audio.loop && isFinite(audio.duration) && audio.duration > this.FADE_DURATION) {
      const fadeAt = (audio.duration - this.FADE_DURATION) * 1000
      entry.fadeTimer = setTimeout(() => {
        if (this.activeSounds.has(soundId)) {
          const now = ctx.currentTime
          graph.gainNode.gain.setValueAtTime(graph.gainNode.gain.value, now)
          graph.gainNode.gain.linearRampToValueAtTime(0, now + this.FADE_DURATION)
        }
      }, fadeAt)
    }

    const onEnded = () => {
      audio.removeEventListener("ended", onEnded)
      this.endedHandlers.delete(soundId)
      const active = this.activeSounds.get(soundId)
      if (active) {
        if (active.fadeTimer) clearTimeout(active.fadeTimer)
        this.activeSounds.delete(soundId)
        this._setPlaying(soundId, false)
      }
    }
    audio.addEventListener("ended", onEnded)
    this.endedHandlers.set(soundId, onEnded)
  }

  _stop(soundId, { fade = this.FADE_DURATION } = {}) {
    const active = this.activeSounds.get(soundId)
    if (!active) return

    if (active.fadeTimer) clearTimeout(active.fadeTimer)
    this.activeSounds.delete(soundId)
    this._setPlaying(soundId, false)

    const audio = this.audioElements.get(soundId)
    if (!audio) return

    if (this.endedHandlers.has(soundId)) {
      audio.removeEventListener("ended", this.endedHandlers.get(soundId))
      this.endedHandlers.delete(soundId)
    }

    const graph = this.audioGraphs.get(soundId)
    const ctx = this.audioContext

    if (ctx && fade > 0 && graph) {
      const now = ctx.currentTime
      graph.gainNode.gain.cancelScheduledValues(now)
      graph.gainNode.gain.setValueAtTime(graph.gainNode.gain.value, now)
      graph.gainNode.gain.linearRampToValueAtTime(0, now + fade)

      const timer = setTimeout(() => {
        audio.pause()
        audio.currentTime = 0
        this.fadeStopTimers.delete(soundId)
      }, fade * 1000 + 50)
      this.fadeStopTimers.set(soundId, timer)
    } else {
      audio.pause()
      audio.currentTime = 0
    }
  }

  stopAll() {
    this._getOrCreateContext()
    const ids = [...this.activeSounds.keys()]
    for (const soundId of ids) {
      this._stop(soundId)
    }
  }

  _setPlaying(soundId, playing) {
    const wrapper = this.element.querySelector(`[data-sound-id="${soundId}"][data-soundboard-target="soundButton"]`)
    if (!wrapper) return
    const btn = wrapper.querySelector(".sound-btn")
    if (!btn) return

    if (playing) {
      btn.classList.add("is-playing")
    } else {
      btn.classList.remove("is-playing")
    }
  }

  updateMasterVolume() {
    const val = parseInt(this.masterVolumeTarget.value)
    this.masterGain = val / 100
    this.masterVolumeLabelTarget.textContent = `${val}%`

    for (const [soundId] of this.activeSounds) {
      const settings = this.soundSettings.get(soundId)
      const graph = this.audioGraphs.get(soundId)
      if (graph && settings) {
        graph.gainNode.gain.value = (settings.volume || 0.8) * this.masterGain
      }
    }
  }

  toggleManageMode() {
    this.manageMode = !this.manageMode

    if (this.manageMode) {
      this.manageToggleTarget.classList.remove("bg-gray-700")
      this.manageToggleTarget.classList.add("bg-indigo-600", "text-white")

      this.gridTargets.forEach(grid => {
        grid.classList.add("manage-mode")
        const sortable = Sortable.create(grid, {
          animation: 200,
          ghostClass: "opacity-50",
          onEnd: () => this._onSortEnd(grid)
        })
        this.sortables.push(sortable)
      })
    } else {
      this.manageToggleTarget.classList.add("bg-gray-700")
      this.manageToggleTarget.classList.remove("bg-indigo-600", "text-white")

      this.gridTargets.forEach(grid => grid.classList.remove("manage-mode"))
      this.sortables.forEach(s => s.destroy())
      this.sortables = []
    }
  }

  _onSortEnd(grid) {
    const wrappers = grid.querySelectorAll(".sound-button-wrapper")
    const positions = []
    wrappers.forEach((w, i) => {
      positions.push({ id: parseInt(w.dataset.soundId), position: i + 1 })
    })

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(`/admin/productions/${this.productionIdValue}/sounds/update_positions`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({ positions })
    })
  }

  toggleSettings(event) {
    event.preventDefault()
    event.stopPropagation()

    const soundId = parseInt(event.currentTarget.dataset.soundId)
    this.currentSettingsSoundId = soundId
    const settings = this.soundSettings.get(soundId)
    if (!settings) return

    this.settingsTitleTarget.textContent = `Settings: ${settings.name}`

    const vol = Math.round((settings.volume || 0.8) * 100)
    this.settingsVolumeTarget.value = vol
    this.settingsVolumeLabelTarget.textContent = `${vol}%`

    this.settingsEqLowTarget.value = settings.eq_low || 0
    this.settingsEqLowLabelTarget.textContent = `${settings.eq_low || 0} dB`

    this.settingsEqMidTarget.value = settings.eq_mid || 0
    this.settingsEqMidLabelTarget.textContent = `${settings.eq_mid || 0} dB`

    this.settingsEqHighTarget.value = settings.eq_high || 0
    this.settingsEqHighLabelTarget.textContent = `${settings.eq_high || 0} dB`

    this._setLoopToggle(settings.loop || false)

    this.settingsModalTarget.classList.remove("hidden")
  }

  closeSettings() {
    this.settingsModalTarget.classList.add("hidden")
    this.currentSettingsSoundId = null
  }

  preventClose(event) {
    event.stopPropagation()
  }

  updateSettingsVolume() {
    const val = parseInt(this.settingsVolumeTarget.value)
    this.settingsVolumeLabelTarget.textContent = `${val}%`
    this._applyLiveSettings()
  }

  updateSettingsEq() {
    this.settingsEqLowLabelTarget.textContent = `${this.settingsEqLowTarget.value} dB`
    this.settingsEqMidLabelTarget.textContent = `${this.settingsEqMidTarget.value} dB`
    this.settingsEqHighLabelTarget.textContent = `${this.settingsEqHighTarget.value} dB`
    this._applyLiveSettings()
  }

  toggleSettingsLoop() {
    const btn = this.settingsLoopTarget
    const isOn = btn.classList.contains("bg-indigo-600")
    this._setLoopToggle(!isOn)
  }

  _setLoopToggle(on) {
    const btn = this.settingsLoopTarget
    const knob = btn.querySelector("span")
    if (on) {
      btn.classList.remove("bg-gray-600")
      btn.classList.add("bg-indigo-600")
      knob.classList.remove("translate-x-1")
      knob.classList.add("translate-x-6")
    } else {
      btn.classList.remove("bg-indigo-600")
      btn.classList.add("bg-gray-600")
      knob.classList.remove("translate-x-6")
      knob.classList.add("translate-x-1")
    }
  }

  _applyLiveSettings() {
    if (!this.currentSettingsSoundId) return
    const soundId = this.currentSettingsSoundId
    if (!this.activeSounds.has(soundId)) return
    const graph = this.audioGraphs.get(soundId)
    if (!graph) return

    const volume = parseInt(this.settingsVolumeTarget.value) / 100
    graph.gainNode.gain.value = volume * this.masterGain
    graph.eqLow.gain.value = parseFloat(this.settingsEqLowTarget.value)
    graph.eqMid.gain.value = parseFloat(this.settingsEqMidTarget.value)
    graph.eqHigh.gain.value = parseFloat(this.settingsEqHighTarget.value)
  }

  resetSettings() {
    this.settingsVolumeTarget.value = 80
    this.settingsVolumeLabelTarget.textContent = "80%"
    this.settingsEqLowTarget.value = 0
    this.settingsEqLowLabelTarget.textContent = "0 dB"
    this.settingsEqMidTarget.value = 0
    this.settingsEqMidLabelTarget.textContent = "0 dB"
    this.settingsEqHighTarget.value = 0
    this.settingsEqHighLabelTarget.textContent = "0 dB"
    this._setLoopToggle(false)
    this._applyLiveSettings()
  }

  async saveSettings() {
    const soundId = this.currentSettingsSoundId
    if (!soundId) return

    const volume = parseInt(this.settingsVolumeTarget.value) / 100
    const eqLow = parseFloat(this.settingsEqLowTarget.value)
    const eqMid = parseFloat(this.settingsEqMidTarget.value)
    const eqHigh = parseFloat(this.settingsEqHighTarget.value)
    const loop = this.settingsLoopTarget.classList.contains("bg-indigo-600")

    const settings = this.soundSettings.get(soundId)
    if (settings) {
      settings.volume = volume
      settings.eq_low = eqLow
      settings.eq_mid = eqMid
      settings.eq_high = eqHigh
      settings.loop = loop
    }

    this._applyLiveSettings()

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      await fetch(`/admin/productions/${this.productionIdValue}/sounds/${soundId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          sound: { volume, eq_low: eqLow, eq_mid: eqMid, eq_high: eqHigh, loop }
        })
      })
    } catch (e) {
      console.error("Failed to save settings:", e)
    }

    this.closeSettings()
  }
}
